/**
 * Değerlendirme state (v0.1.260): GET /evals/temsilci when the Öğrenme screen opens. Read only.
 */

import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import type { EvalView } from '../api/evals.js';
import { useStore } from './context.js';

export interface EvalState {
  view: EvalView | null;
  error: string | null;
  loading: boolean;
}

export class EvalStore {
  private state: EvalState = { view: null, error: null, loading: false };
  private inFlight: Promise<void> | null = null;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): EvalState => this.state;

  private set(patch: Partial<EvalState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  /** One read at a time; the current view stays while it loads. */
  reload(): Promise<void> {
    if (this.inFlight !== null) return this.inFlight;
    this.set({ loading: true });
    const run = (async () => {
      try {
        this.set({ view: await this.api.getEvals(), error: null, loading: false });
      } catch (error) {
        this.set({ error: error instanceof Error ? error.message : String(error), loading: false });
      }
    })();
    this.inFlight = run;
    void run.finally(() => {
      if (this.inFlight === run) this.inFlight = null;
    });
    return run;
  }
}

export function useEvals(): { store: EvalStore; state: EvalState } {
  const store = useStore().evals;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
